import React, { useContext, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Box, Flex, Grid, Link } from "theme-ui";
import { useNavigate } from "react-router-dom";

import { createService } from "api/Services";
import { trackActivity } from "api/ActivityTracker";

import { filterByKeys } from "helpers/default";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { BackArrowIcon, CheckMarkIcon } from "components/svg";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import { themevals } from "theme/themevals";
import UserContext from "context/User";

export default function ServiceCreate () {
  const navigate = useNavigate();
  const toast = useToast();  
  const { user } = useContext<any>(UserContext);

  const submitCreate = async (values: any) => {   
    const keys = ["name","description"];    
    const create = filterByKeys(keys,values);
    
    const res = await createService({ create });
    if(res?.id){ 
      toast.add("Service created.","var(--theme-ui-colors-success)");   
      void trackActivity({
        module: "services",
        action: "create_service",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "service",
        entityId: res?.id ?? null,
        metadata: null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      navigate(`/services/${res?.id}`)
    }else{
      toast.add(res?.message ? res.message : "Error creating service","var(--theme-ui-colors-danger)");
    }                 
  };  


  const _validate = Yup.object().shape({
    name: Yup.string().required('This field is required.'),
  });

  useEffect(() => {    
    document.title = process.env.REACT_APP_NAME + " | Service"
  }, []); 

  return (
    <>
      <Header></Header>      
      <Page 
        title="Add service" 
        subtitle="Add a service to your company." 
        backAction={<Link variant="back" href="/services"><BackArrowIcon />Back to Services</Link>}
      >                
        <Formik initialValues={{}} onSubmit={submitCreate} validationSchema={_validate}>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
            <Form noValidate autoComplete="off">   
              <Tabs
                vertical
                stickyFooter
                footer={<Flex sx={{gap: "10px", flexGrow : 1, justifyContent: "flex-end"}}>                
                  <Link href="/services" sx={{...themevals.buttons.secondaryOutline, textDecoration: "none", margin:"0 10px 0 0"}}>Cancel</Link>
                  <BasicButton 
                    type="submit" 
                    $submitting={isSubmitting}
                    styles={{margin:"0 10px 0 0"}}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"}/>}
                    Save service
                  </BasicButton>
                </Flex>}
              >
                <Tab itemKey="general">
                  <Grid gap={6} columns={[1, '1fr 3fr']} sx={{paddingLeft:"30px", paddingRight:"30px"}}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>Basic Info</H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>Add main service information.</Subtext>
                    </Box> 
                    <Box>
                      <Flex sx={{flexDirection : ["column","row"], gap: "1rem", marginBottom : "1rem"}}>    
                        <TextInput                        
                          name="name"
                          label="Name"
                          value={values.name}                        
                          onChange={(val: any) => setFieldValue("name",val)}
                          $errors={errors.name && submitCount > 0 ? errors.name : null}    
                          $customStyles={{flexGrow: 1, width: "100%"}}
                        />                                               
                        <TextInput                        
                          name="description"
                          label="Description"
                          value={values.description}                        
                          onChange={(val: any) => setFieldValue("description",val)}
                          $errors={errors?.description && submitCount > 0 ? errors?.description : null}   
                          $customStyles={{flexGrow: 1, width: "100%"}} 
                        />
                      </Flex>                         
                    </Box>     
                  </Grid>                                                   
                </Tab>                   
              </Tabs>                                        
            </Form>
            )
          }
        </Formik>
      </Page>
    </>
  );
}
